// src/pages/Admin/Recintos/index.jsx
import React, { useState, useEffect } from "react";
import { Table, Input, Dropdown, App, Select } from "antd";
import {
  EditOutlined, DeleteOutlined, EyeOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined,
} from "@ant-design/icons";
import { FiMapPin, FiMoreVertical, FiDownload, FiPlus, FiLayers, FiCheckCircle, FiPauseCircle, FiSearch } from "react-icons/fi";
import {
  EncabezadoAdmin, TarjetaResumen, ChipEstado, ChipNeutro, claseBtnPrimario, claseBtnSecundario,
} from "../../../Ui/Admin/PiezasAdmin";
import { formatearNombre } from "../../../../utils/texto";
import { useNavigate } from "react-router-dom";
import Recintos from "../../../../service/Recintos";
import { useNotify, useWithNotify } from "../../../../providers/NotificationProvider";
import useAuthContext from "../../../../hooks/useAuthContext";
import { exportarHojaUnica } from "../../../../utils/exportExcel";
import dayjs from "dayjs";
import "dayjs/locale/es";

dayjs.locale("es");


export default function ListaRecintos() {
  const [recintos, setRecintos] = useState([]);
  const [estadoFiltro, setEstadoFiltro] = useState("all");
  const [totalRecintos, setTotalRecintos] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("all");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const navigate = useNavigate();
  const notify = useNotify();
  const withNotify = useWithNotify();
  const { modal } = App.useApp();
  const { user } = useAuthContext();

  // Conteos de TODOS los recintos. Antes se contaban sobre la página visible (10
  // filas), así que "8 activos · 2 inactivos" no sumaba el total de 14.
  const [conteo, setConteo] = useState({ activos: 0, inactivos: 0 });
  const loadConteo = async () => {
    try {
      const res = await Recintos.List({ limit: 1000 });
      const todos = res.data || [];
      const activos = todos.filter(r => r.estado === 'activo').length;
      setConteo({ activos, inactivos: todos.length - activos });
    } catch { /* sin conteo no se rompe la pantalla */ }
  };
  useEffect(() => { loadConteo(); }, []);

  const loadRecintos = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
      };

      if (estadoFiltro && estadoFiltro !== "all") params.estado = estadoFiltro;
      if (tipoFiltro && tipoFiltro !== "all") params.tipo = tipoFiltro;
      // Si usas estado y comuna, agregarlos también si tienen valor
      // Ejemplo (agrega estados si los tienes como filtro)
      // if (estadoFiltro) params.estado = estadoFiltro;
      // if (comunaFiltro) params.comuna = comunaFiltro;

      const response = await Recintos.List(params);
    
      const recintosData = response.data || [];
      const totalRecintos = response.total || 0;
      setTotalRecintos(totalRecintos);

      const recintosOrdenados = recintosData.sort((a, b) =>
        (a.nombre || a.label || '').localeCompare(b.nombre || b.label || '', 'es', { sensitivity: 'base' })
      );

      setRecintos(recintosOrdenados);
      setPagination(prev => ({ ...prev, total: totalRecintos }));
    } catch (error) {
      notify.error("No se pudieron cargar los recintos");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTipoChange = (value) => {
    setTipoFiltro(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleDelete = (recinto) => {
    modal.confirm({
      title: "¿Eliminar recinto?",
      icon: <ExclamationCircleOutlined />,
      content: `¿Estás seguro de eliminar el recinto "${recinto.nombre}"? Esta acción no se puede deshacer.`,
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await withNotify(
            Recintos.Delete(recinto._id),
            {
              loading: "Eliminando recinto...",
              success: "Recinto eliminado exitosamente",
              error: "No se pudo eliminar el recinto"
            }
          );
          loadRecintos(); loadConteo();
        } catch (error) {
          console.error("Error eliminando recinto:", error);
        }
      },
    });
  };

  const handleEstadoChange = (value) => {
    setEstadoFiltro(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadRecintos();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchText, tipoFiltro, estadoFiltro, pagination.current]);

  const handleCambiarEstado = async (recinto) => {
    const nuevoEstado = recinto.estado === "activo" ? "inactivo" : "activo";

    try {
      await withNotify(
        Recintos.CambiarEstado(recinto._id, nuevoEstado),
        {
          loading: `Cambiando estado a ${nuevoEstado}...`,
          success: `Recinto ${nuevoEstado === "activo" ? "activado" : "desactivado"} exitosamente`,
          error: "No se pudo cambiar el estado"
        }
      );
      loadRecintos(); loadConteo();
    } catch (error) {
      console.error("Error cambiando estado:", error);
    }
  };

  const TIPOS = {
    polideportivo: "Polideportivo", gimnasio: "Gimnasio", piscina: "Piscina", cancha: "Cancha",
    sala: "Sala", otro: "Otro", calle_avenida: "Calle o Avenida", colegio_liceo: "Colegio o Liceo",
    multicancha: "Multicancha", parque: "Parque", pista_atletica: "Pista Atlética", plaza: "Plaza",
    sede_vecinal: "Sede Vecinal", skate_park: "Skate Park",
  };

  // "SIN DIRECCIÓN" y similares vienen escritos como dato: se tratan como vacío.
  const direccionReal = (d) => (d && !/^sin direcci[oó]n$/i.test(d.trim()) ? d : null);

  const columns = [
    {
      title: "Recinto",
      dataIndex: "nombre",
      key: "nombre",
      render: (text, record) => {
        const dir = direccionReal(record.direccion);
        return (
          <div className="flex items-center gap-3 min-w-0">
            <span className={`w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center
              ${record.estado === 'activo' ? 'bg-blue-50 text-primary' : 'bg-gray-100 text-gray-400'}`}>
              <FiMapPin size={16} />
            </span>
            <div className="min-w-0">
              <div className="font-bold text-gray-900 truncate">{formatearNombre(text)}</div>
              <div className={`text-xs truncate max-w-[440px] ${dir ? 'text-gray-500' : 'text-gray-400 italic'}`} title={dir || undefined}>
                {dir ? `${dir}${record.comuna && record.comuna !== 'Ñuñoa' ? ` · ${record.comuna}` : ''}` : 'Sin dirección'}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Tipo",
      dataIndex: "tipo",
      key: "tipo",
      width: 180,
      render: (t) => <ChipNeutro>{TIPOS[t] || TIPOS.otro}</ChipNeutro>,
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      width: 130,
      render: (estado) => <ChipEstado activo={estado === "activo"} />,
    },
    {
      title: "Creado",
      dataIndex: "creadoEn",
      key: "creadoEn",
      width: 130,
      render: (date) => <span className="text-sm text-gray-500 tabular-nums">{dayjs(date).format("DD/MM/YYYY")}</span>,
    },
    {
      title: <span className="sr-only">Acciones</span>,
      key: "acciones",
      width: 64,
      align: "center",
      // El span corta el clic: el menú vive en un portal pero sus eventos igual suben
      // por el árbol de React hasta la fila, que abriría el detalle.
      render: (_, record) => (
        <span onClick={e => e.stopPropagation()}>
        <Dropdown
          trigger={["click"]}
          placement="bottomRight"
          menu={{
            items: [
              { key: "ver", icon: <EyeOutlined />, label: "Ver detalle", onClick: () => navigate(`/admin/recintos/${record._id}`) },
              { key: "editar", icon: <EditOutlined />, label: "Editar", onClick: () => navigate(`/admin/recintos/${record._id}/editar`) },
              {
                key: "estado",
                icon: record.estado === "activo" ? <CloseCircleOutlined /> : <CheckCircleOutlined />,
                label: record.estado === "activo" ? "Desactivar" : "Activar",
                onClick: () => handleCambiarEstado(record),
              },
              { type: "divider" },
              {
                key: "eliminar", icon: <DeleteOutlined />, danger: true,
                label: record.estado === "activo" ? "Eliminar (desactívalo primero)" : "Eliminar",
                disabled: record.estado === "activo",
                onClick: () => handleDelete(record),
              },
            ],
          }}
        >
          <button type="button" aria-label={`Acciones de ${record.nombre}`}
            className="w-9 h-9 inline-flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors">
            <FiMoreVertical size={17} />
          </button>
        </Dropdown>
        </span>
      ),
    },
  ];

  const totalGeneral = conteo.activos + conteo.inactivos || totalRecintos;
  const hayFiltros = searchText || tipoFiltro !== "all" || estadoFiltro !== "all";

  const handleExportarExcel = async () => {
    try {
      // Todos los que cumplen los filtros, no solo la página visible.
      const params = { limit: 1000, search: searchText };
      if (estadoFiltro !== "all") params.estado = estadoFiltro;
      if (tipoFiltro !== "all") params.tipo = tipoFiltro;
      const filas = (await Recintos.List(params)).data || [];
      if (!filas.length) { notify.warning('No hay recintos para exportar'); return; }
      await exportarHojaUnica({
        archivo: 'recintos',
        titulo: 'Recintos',
        subtitulo: 'Listado de recintos registrados en el sistema',
        usuario: user,
        filtros: {
          'Búsqueda': searchText || null,
          'Estado': estadoFiltro !== 'all' ? estadoFiltro : 'Todos',
          'Tipo': tipoFiltro !== 'all' ? tipoFiltro : 'Todos',
        },
        nombreHoja: 'Recintos',
        columnas: [
          { header: 'Nombre', key: 'nombre', tipo: 'texto', ancho: 32 },
          { header: 'Dirección', key: 'direccion', tipo: 'texto', ancho: 38 },
          { header: 'Comuna', key: 'comuna', tipo: 'texto' },
          { header: 'Tipo', key: 'tipo', tipo: 'texto' },
          {
            header: 'Estado', key: 'estado', tipo: 'texto',
            resaltar: (r) => (r.estado === 'activo' ? 'ok' : 'alerta'),
          },
          { header: 'Fecha de creación', key: 'creadoEn', tipo: 'fecha' },
        ],
        filas: filas.map(r => ({
          nombre: r.nombre,
          direccion: r.direccion,
          comuna: r.comuna,
          tipo: r.tipo,
          estado: r.estado,
          creadoEn: r.creadoEn,
        })),
      });
    } catch { notify.error('Error al generar Excel'); }
  };

  return (
    <div>
      <EncabezadoAdmin
        titulo="Gestión de"
        destacado="recintos"
        subtitulo={`${totalGeneral.toLocaleString('es-CL')} recintos · ${conteo.activos} activos · ${conteo.inactivos} inactivos`}
      >
        <button type="button" onClick={handleExportarExcel} disabled={!totalRecintos} className={claseBtnSecundario}>
          <FiDownload size={15} /> Exportar Excel
        </button>
        <button type="button" onClick={() => navigate("/admin/recintos/crear")} className={claseBtnPrimario}>
          <FiPlus size={16} /> Nuevo recinto
        </button>
      </EncabezadoAdmin>

      {/* Resumen: cada tarjeta filtra la tabla por estado */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <TarjetaResumen icon={<FiLayers size={19} />} label="Todos" value={totalGeneral}
          activa={estadoFiltro === "all"} onClick={() => handleEstadoChange("all")} />
        <TarjetaResumen icon={<FiCheckCircle size={19} />} label="Activos" value={conteo.activos}
          activa={estadoFiltro === "activo"} onClick={() => handleEstadoChange("activo")} />
        <TarjetaResumen icon={<FiPauseCircle size={19} />} label="Inactivos" value={conteo.inactivos}
          activa={estadoFiltro === "inactivo"} onClick={() => handleEstadoChange("inactivo")} />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3 p-3 mb-4 rounded-2xl bg-white border border-gray-100">
        <Input
          allowClear
          prefix={<FiSearch className="text-gray-400" />}
          placeholder="Buscar por nombre, dirección o comuna…"
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          className="!rounded-full"
          style={{ maxWidth: 340 }}
          aria-label="Buscar recintos"
        />
        <Select value={tipoFiltro} onChange={handleTipoChange} style={{ minWidth: 190 }} aria-label="Tipo de recinto"
          options={[{ value: "all", label: "Todos los tipos" }, ...Object.entries(TIPOS)
            .sort((a, b) => a[1].localeCompare(b[1], 'es'))
            .map(([value, label]) => ({ value, label }))]} />
        {hayFiltros && (
          <button type="button" onClick={() => { handleSearch(""); handleTipoChange("all"); handleEstadoChange("all"); }}
            className="text-sm font-semibold text-gray-500 hover:text-primary">
            Limpiar filtros
          </button>
        )}
        <span className="ml-auto text-xs font-semibold text-gray-500">
          {totalRecintos.toLocaleString('es-CL')} resultado{totalRecintos !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="admin-table-card">
        <Table
          columns={columns}
          dataSource={recintos}
          loading={loading}
          rowKey="_id"
          sticky
          rowClassName={() => "fila-clicable"}
          onRow={(record) => ({ onClick: () => navigate(`/admin/recintos/${record._id}`) })}
          locale={{
            emptyText: (
              <div className="py-10 text-center">
                <p className="font-bold text-gray-900 m-0">No hay recintos con estos filtros</p>
                <p className="text-sm text-gray-500 m-0 mt-1">Prueba con otra búsqueda o limpia los filtros.</p>
              </div>
            ),
          }}
          pagination={{
            ...pagination,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} recintos`,
            style: { padding: '12px 16px' },
          }}
          onChange={handleTableChange}
          scroll={{ x: 820 }}
        />
      </div>
    </div>
  );
}
